import "./app-navbar.scss";
import "intro.js/introjs.css";

import { bindingMode, computedFrom, observable } from "aurelia-binding";
import { autoinject } from "aurelia-dependency-injection";
import { DialogOpenResult, DialogService, DialogSettings } from "aurelia-dialog";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { NavModel, RouteConfig, Router } from "aurelia-router";
import { bindable, customElement } from "aurelia-templating";
import { User as OidcUser, UserManager } from "oidc-client";

import { config } from "../../common/config";
import nameof from "../../common/nameof";
import { OidcSignOut, UserLoaded } from "../../common/oidc/oidc-event-handler";
import { isRouteAllowed } from "../../common/utilities/is-route-allowed";
import { PermissionManager, PermissionEvent } from "../../common/utilities/permission-manager";
import { Alert, IAlertOptions } from "../../resources/dialogs/alert/alert";
import { IPromptOptions, Prompt } from "../../resources/dialogs/prompt/prompt";
import { AccountsService } from "../../services/accounts-service";
import { AccountEvent, AuthService } from "../../services/auth-service";
import { ToastrService } from "../../services/toastr-service";
import { SignalrReactor } from "../../signalr/signalr-reactor";
import { Permission } from "../../models/permission";
import { SignalRService } from "../../signalr/signalr-service";
import { SignalRHubs } from "../../enums/signalr-hubs";
import { NotificationsHubEvents } from "../../enums/signalr-notification-events";
import { INotification } from "../../interfaces/i-signalr-notifications";
import Infrastructure from "../../common/infrastructure";
import { UsersService } from "../../services/users-service";
import { AppCache } from "./app-cache";
import moment from "moment";
import { User } from "../../models/user";
import { AccountStore, AccountStoreEvents } from "../../common/stores/account-store";
import { CurrentUserService } from "../../services/current-user-service";
import { AdministrativePermissionEnum } from "../../enums/administrative-permission-enum";
import { PermissionActionEnum } from "../../enums/permission-action-enum";
import { AiService } from "../../services/ask-axxess-service";
import { TakeOverScreenForDashboard } from "../../enums/take-over-screen-for-tutorial";
import introJs from "intro.js";
import { AiUsageStore } from "../../common/stores/ai-usage-store";

@autoinject()
@customElement("app-navbar")
export class AppNavbar {
    private readonly NAV_TOUR_KEY = "axxess-nav-tour-seen";
    private _toastrService: ToastrService;
    private _accountsService: AccountsService;
    private _authService: AuthService;
    private _signalR: SignalrReactor;
    private _ea: EventAggregator;
    private _dialogService: DialogService;
    private _userManager: UserManager;
    private _user: OidcUser;
    private _subscriptions: Subscription[] = [];
    private _aiUsageStore: AiUsageStore;
    private _permissionManager: PermissionManager;
    private _signalRService: SignalRService;
    private _usersService: UsersService;
    private _currentUserService: CurrentUserService;
    private _aiService: AiService;
    private _appCache: AppCache;
    private _versionChanged: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public isAccountLoading: boolean;
    public router: Router;
    public userFirstName: string;
    public originUrl: string = window.location.origin;
    public originName: string = "AxxessHospice";
    public releaseVersion: string = Infrastructure.releaseVersion;
    public isLoggedIn: boolean = false;
    public isPalliativeCare: boolean = false;
    public accountName: string = "";
    public helpUrl: string = config.helpUrl;
    public softwareUpdateUrl: string = config.softwareUpdateUrl;
    public trainingWebinarUrl: string = config.trainingWebinarUrl;
    public hasMultipleAccounts: boolean = false;
    public isSearchExpanded: boolean = false;
    @observable({ changeHandler: nameof<AppNavbar>("accountIdChanged") })
    public accountId: string;
    public userPermissionLoaded: boolean = false;
    public providersLoaded: boolean = false;
    public axxessCentralUrl: string = Infrastructure.axxessCentral;
    public userCommunityUrl: string = config.userCommunityUrl;
    public followAxxessUrl: string = config.followAxxessUrl;
    public hasMultipleProviders: boolean = false;
    public axxessChatIsEnabled: boolean = false;
    public currentUserInfo: User;
    public selectAccountRoute: string = "oidc-select-accounts";
    public isMenuOpen: boolean = false;
    public isRightMenuOpen: boolean = false;
    public axxessIntelligenceTab: HTMLElement;
    public intro: any;
    public tutorialStarted: boolean = false;

    @computedFrom(`${nameof<AppNavbar>("router")}.${nameof<Router>("navigation")}`)
    public get activeRouteName(): string {
        if (this.router) {
            let activeRoute: NavModel = this.router.navigation.find((route) => route.isActive);
            return activeRoute ? activeRoute.settings.menuName : "";
        } else {
            return "";
        }
    }

    @computedFrom(nameof<AppNavbar>("router"))
    public get switchAccountsUrl() {
        let selectUrl = this.router.generate(this.selectAccountRoute, {
            fragment: this.router.currentInstruction.fragment
        });
        return selectUrl;
    }

    public get axxessIntelligenceRoute() {
        return this.router.navigation.find((nav) => nav.settings.menuName === "Axxess Intelligence");
    }

    @computedFrom(nameof<AppNavbar>("userPermissionLoaded"))
    public get hasAskAxxessViewPermission() {
        if (!this.userPermissionLoaded) {
            return false;
        }
        return this._permissionManager.checkPermission([
            { resource: AdministrativePermissionEnum.AskAxxess, action: PermissionActionEnum.View }
        ]);
    }

    public constructor(
        accountService: AccountsService,
        userManager: UserManager,
        signalR: SignalrReactor,
        signalRService: SignalRService,
        authService: AuthService,
        ea: EventAggregator,
        router: Router,
        dialogService: DialogService,
        toastrService: ToastrService,
        permissionManager: PermissionManager,
        usersService: UsersService,
        currentUserService: CurrentUserService,
        appCache: AppCache,
        aiService: AiService,
        aiUsageStore: AiUsageStore
    ) {
        this._toastrService = toastrService;
        this._accountsService = accountService;
        this._signalR = signalR;
        this._signalRService = signalRService;
        this._authService = authService;
        this._ea = ea;
        this._userManager = userManager;
        this.router = router;
        this._dialogService = dialogService;
        this._permissionManager = permissionManager;
        this._usersService = usersService;
        this._currentUserService = currentUserService;
        this._appCache = appCache;
        this._aiService = aiService;
        this._aiUsageStore = aiUsageStore;
        this.initSubscriptions();
    }

    private initSubscriptions() {
        this._subscriptions.push(this._ea.subscribe(UserLoaded, () => this.loadUser()));
        this._subscriptions.push(
            this._ea.subscribe(AccountEvent.Selected, async (accountId: string) => {
                // hide axxess chat on account change
                this.axxessChatIsEnabled = false;
                this.userPermissionLoaded = false;
                await this.processSelectedAccount(accountId);
                // refresh permissions after selecting new provider
                await this._permissionManager.refreshUserPermissionData(true);
            })
        );
        this._subscriptions.push(
            this._ea.subscribe(PermissionEvent.UserPermissionLoaded, async (permissions: Permission[]) => {
                if (permissions?.length > 0) {
                    this.userPermissionLoaded = true;
                    await this.aiTutorialSeenFlag();
                }
            })
        );
    }

    public async attached() {
        await this.loadUser();
        // Do not load account info if we are not currently selecting an account
        if (!window.location.href.includes(this.router.generate(this.selectAccountRoute))) {
            let accountId = this._authService.getAccountId();
            await this._aiUsageStore.initialize(accountId);
            if (!!accountId && this.isLoggedIn && this._user) {
                await this.processSelectedAccount(accountId);
                await this.initSignalRForAxxessChat();
                await this.aiTutorialSeenFlag();
                this.loadNavbarNotifications();
            }
        }
    }

    public async accountIdChanged(newValue: string, oldValue: string) {
        if (newValue !== oldValue) {
            await this._aiUsageStore.initialize(newValue);
        }
    }

    public async aiTutorialSeenFlag() {
        try {
            if (this.tutorialStarted) {
                return;
            }
            this.tutorialStarted = true;
            let response = await this._aiService.aiTourSeen();
            if (this.hasAskAxxessViewPermission && !response.hasAskAxxessTutorialFlag && !response.isSupportUser) {
                requestAnimationFrame(() =>
                    requestAnimationFrame(() => {
                        this.startStepOne();
                    })
                );
            }
        } catch (e) {
            const errorMsg = await e.response.json();
            if (e.status === 403) {
                console.log(errorMsg.message);
                return;
            }
        }
    }

    private async autoUpdateAiTutorial() {
        const askAxxessFlag = TakeOverScreenForDashboard.AskAxxess;
        this._usersService.updateTakeoverShown({ takeOverScreenFlag: askAxxessFlag });
    }

    public startStepOne() {
        if (localStorage.getItem(this.NAV_TOUR_KEY) === "true") {
            return;
        }
        if (!this.axxessIntelligenceTab) {
            return;
        }
        const rect = this.axxessIntelligenceTab.getBoundingClientRect();
        if (!rect.width || !rect.height) {
            return;
        }
        const img1 = require(`../../assets/images/ai-tour/aiTour1.png`);
        const img2 = require(`../../assets/images/ai-tour/axxess-intelligence-search.svg`);
        this.intro = introJs();
        this.intro.setOptions({
            showButtons: false,
            showStepNumbers: false,
            exitOnOverlayClick: false,
            exitOnEsc: false,
            overlayOpacity: 0.6,
            steps: [
                {
                    element: this.axxessIntelligenceTab,
                    position: "bottom",
                    intro: `
                    <div class="ask-tour-card">
                        <p class="tour-title">
                            Save time and boost productivity instantly
                            <br/>
                            with <strong>Ask Axxess</strong>
                        </p>
                        <div class="tour-gradient-box">
                            <img src="${img1}" alt="axxess-img" class="ai-gradient-bg"/>
                            <img src="${img2}" alt="axxess-img" class="ai-search-img"/>
                        </div>
                        <div class="tour-actions">
                            <button class="btn btn-sm btn-outline-primary mr-2 tour-skip">Skip</button>
                            <button class="btn btn-sm btn-outline-primary tour-explore">Explore</button>
                        </div>
                    </div>
                    `
                }
            ]
        });

        this.intro.start();
        setTimeout(() => {
            document.querySelector(".tour-skip")?.addEventListener("click", async () => {
                localStorage.setItem(this.NAV_TOUR_KEY, "true");
                await this.autoUpdateAiTutorial();
                this.intro.exit();
            });
            document.querySelector(".tour-explore")?.addEventListener("click", () => {
                localStorage.setItem(this.NAV_TOUR_KEY, "true");
                this.intro.exit();
                this.router.navigate(`/axxess-intelligence/axxess-intelligence-page?tour=true`);
            });
        }, 50);
    }

    private async loadUser() {
        this._user = await this._userManager.getUser();
        if (this._user) {
            this.isLoggedIn = await this._authService.isLoggedIn();
            this.userFirstName = this._user.profile.given_name;
        }
    }

    private async processSelectedAccount(accountId: string) {
        this.isAccountLoading = true;
        this.accountId = accountId;
        this.accountName = "";
        let hasCachedUserConflict = await this._authService.hasCachedUserConflict();
        if (!hasCachedUserConflict) {
            await this.loadAccount();
            try {
                let response = await this._accountsService.getAxxessChatStatus(accountId);
                this.axxessChatIsEnabled = response?.isAxxessChatEnabled;
            } catch (error) {
                console.log(error);
            }
            this._ea.publish(AccountEvent.Loaded);
        }
        this.isAccountLoading = false;
    }

    private async initSignalRForAxxessChat() {
        try {
            await this._signalRService.addHub(
                SignalRHubs.NotificationsHub,
                `${Infrastructure.messagingServerUrl}/notification`
            );
            console.info(`Starting ${SignalRHubs.NotificationsHub}...`);
            await this._signalRService.startHub(SignalRHubs.NotificationsHub);
            console.info(`${SignalRHubs.NotificationsHub} started successfully.`);
        } catch (error) {
            console.error(error);
        }
    }

    private loadNavbarNotifications() {
        this._signalRService.on(
            SignalRHubs.NotificationsHub,
            NotificationsHubEvents.ReceiveMessage,
            (notification: INotification) => {
                this._ea.publish(NotificationsHubEvents.LoadNavbarNotifications, notification);
            }
        );
    }

    private async loadAccount() {
        try {
            let account = await this._accountsService.getAccountInfo(true);
            if (account == null) {
                this.accountName = "Unknown Account";
            } else {
                this.accountName = account.name;
                this.hasMultipleProviders = account.providers?.length > 1;
                this.isPalliativeCare = await this._accountsService.getIsPalliativeAccount();
                AccountStore.providers = account.providers;
                AccountStore.featureToggles = account.featureToggles;
                this._ea.publish(AccountStoreEvents.Loaded);
            }
            let currentUserDetails = await this._currentUserService.getUserDetails();
            this.hasMultipleAccounts = currentUserDetails.hasMultipleAccounts;
            await this.initPlugins(this.accountId);
        } catch (error) {
            let isLoggedIn = await this._authService.isLoggedIn();
            if (isLoggedIn) {
                if (error.status === 401) {
                    let model: IAlertOptions = {
                        title: `Unauthorized User`,
                        message: `User with email <b> ${this._user.profile.email} </b> is not authorized or Invalid user credentials`
                    };
                    let options: DialogSettings = {
                        viewModel: Alert,
                        model: model
                    };
                    await this._dialogService.open(options).whenClosed(async () => {
                        await this.logOut();
                    });
                } else {
                    throw error;
                }
            }
        }
    }

    private async initPlugins(accountId: string) {
        if (!!accountId) {
            try {
                let hasVersionChanged = this.checkIfVersionChanged(this.releaseVersion);
                if (hasVersionChanged) {
                    await this.handleVersionChange();
                } else {
                    // This is to prevent not caching data when the app versions are same
                    await this._appCache.restoreMissingCache();
                }
                await this._signalR.start(accountId);
                let hasWeekendAccess = await this.isUserAllowedAccess();
                if (!hasWeekendAccess) {
                    await this.showPromptMessage();
                    return;
                }
            } catch (e) {
                console.error(e);
            }
        }
    }

    private checkIfVersionChanged(version: string) {
        let cachedAppVersion = localStorage.getItem(config.versionCacheKey);
        this._versionChanged = !cachedAppVersion || (version && cachedAppVersion !== version);
        return this._versionChanged;
    }

    private async handleVersionChange() {
        if (this._versionChanged) {
            localStorage.setItem(config.versionCacheKey, this.releaseVersion);
            await this._appCache.refreshCache();
        }
    }

    private async isUserAllowedAccess() {
        let currentUser = await this._currentUserService.getUser();
        if (!currentUser) {
            return false;
        }
        this.currentUserInfo = await this._usersService.getUser(currentUser.id);
        return this.isUserAllowedDuringTime() && this.isUserAllowedWeekend();
    }

    private isUserAllowedWeekend() {
        if (!this.currentUserInfo) {
            return false;
        }
        let currentWeekDay = moment().format("dddd");
        let isWeekend = currentWeekDay.toLowerCase() === "sunday" || currentWeekDay === "saturday";
        if (isWeekend && !this.currentUserInfo?.allowWeekendAccess) {
            return false;
        }
        return true;
    }

    private isUserAllowedDuringTime() {
        if (!this.currentUserInfo) {
            return false;
        }
        if (!this.currentUserInfo.earliestLoginTime && !this.currentUserInfo.automaticLogoutTime) {
            return true;
        }
        let timeToCompare = moment(moment(), "HH:mm");
        let isBeforeAllowedLogIn = timeToCompare.isBefore(moment(this.currentUserInfo.earliestLoginTime, "HH:mm"));
        let isAfterAllowedLogOut = timeToCompare.isAfter(moment(this.currentUserInfo.automaticLogoutTime, "HH:mm"));
        if (isBeforeAllowedLogIn || isAfterAllowedLogOut) {
            return false;
        }
        return true;
    }

    private async showPromptMessage() {
        let promptOptions: IPromptOptions = {
            message: "Account access is restricted based on your user settings.",
            additionalMessage:
                "If you need access to this account, contact your administrator to update your login restrictions.",
            okText: "Ok",
            cancelText: ""
        };

        let dialogOptions: DialogSettings = {
            viewModel: Prompt,
            model: promptOptions
        };

        let dialog = (await this._dialogService.open(dialogOptions)) as DialogOpenResult;
        let closeResult = await dialog.closeResult;

        if (!closeResult.wasCancelled) {
            this.router.navigate(this.axxessCentralUrl);
        }
    }

    public async logOut() {
        let promptOptions: IPromptOptions = {
            title: "Log Out",
            message: "Do you want to log out?",
            okText: "Yes, Log Out",
            cancelText: "No"
        };

        let dialogOptions: DialogSettings = {
            viewModel: Prompt,
            model: promptOptions
        };

        let dialog = (await this._dialogService.open(dialogOptions)) as DialogOpenResult;
        let closeResult = await dialog.closeResult;

        if (!closeResult.wasCancelled) {
            await this._signalR.stop();
            this._ea.publish(OidcSignOut);
        }
    }

    public closeNotifications() {
        this._toastrService.clear();
    }

    public isLinkAllowed(routeConfig: RouteConfig) {
        let isPalliativeRoute = routeConfig?.settings?.isPalliativeCare;
        if (
            (isPalliativeRoute === false && this.isPalliativeCare) ||
            (isPalliativeRoute === true && !this.isPalliativeCare)
        ) {
            return false;
        }
        if (routeConfig) {
            return isRouteAllowed(routeConfig);
        } else {
            return false;
        }
    }

    public isMenuAllowed(route: NavModel) {
        if (route) {
            return this.isLinkAllowed(route.config);
        } else {
            return false;
        }
    }

    public hasPermission(routeConfig: RouteConfig) {
        if (!routeConfig) {
            return false;
        }
        if (!!routeConfig?.settings?.permission) {
            return this._permissionManager.checkRoutePermission(routeConfig);
        } else {
            return true;
        }
    }

    public navigateToAxxessCentral() {
        this.router.navigate(this.axxessCentralUrl + "/clinician");
    }

    public toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen;
        if (this.isMenuOpen) {
            this.isRightMenuOpen = false;
        }
        $(document).on("click", (e) => {
            if ($(e.target).is(".axxess-intelligence-input")) {
                $(".collapse").collapse("hide");
            }
        });
    }

    public toggleRightMenu() {
        this.isRightMenuOpen = !this.isRightMenuOpen;
        if (this.isRightMenuOpen) {
            this.isMenuOpen = false;
        }
    }

    public detached() {
        if (this._subscriptions.length > 0) {
            this._subscriptions.forEach((sub) => sub.dispose());
        }
    }

    public navigateToAxxessDashboard() {
        this.router.navigate(this.axxessCentralUrl + "/axxess-central/Redirect?agencyId=" + this.accountId);
    }
}
